import * as RadixAccordion from '@radix-ui/react-accordion';
import { ChevronDownIcon } from '@radix-ui/react-icons';
import classNames from 'classnames';
import { forwardRef, useEffect, useState } from 'react';
import { camelCaseToKebabCase } from '../../../functions/utils/camelCaseToKebabCase';
import { TrueFalseTabs } from '../../generic/TrueFalseTabs';
import { StyleProps } from '../props/StyleProps';

export const SidebarAccordionItem = ({
  item,
  name,
  iconClassName,
  toggleItem,
  iconSize,
  pseudoclass,
  overflowHidden,
}: {
  item: string;
  name: string;
  iconClassName?: string;
  toggleItem: (item: string) => void;
  iconSize?: number;
  pseudoclass: string;
  overflowHidden?: boolean;
}) => {
  const [isOverflowHidden, setIsOverflowHidden] = useState(false);

  // To fix problem with overflow (inputs are trimmed)
  useEffect(() => {
    if (overflowHidden === undefined) return;

    const sleep = (ms: number) =>
      new Promise((resolve) => setTimeout(resolve, ms));

    (async () => {
      if (overflowHidden === false) await sleep(300);
      setIsOverflowHidden(overflowHidden);
    })();
  }, [overflowHidden]);

  return (
    <RadixAccordion.Item
      style={{ overflow: isOverflowHidden ? 'hidden' : 'visible' }}
      className="AccordionItem"
      value={item}
      onClick={() => toggleItem(item)}
    >
      <AccordionTrigger iconClassName={iconClassName} iconSize={iconSize}>
        <span>{name}</span>
      </AccordionTrigger>
      <AccordionContent>
        <div
          onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
        >
          {item === 'doesAppOngoingInstallTaskExist' ||
          item === 'doesAppOngoingUpdateTaskExist' ||
          item === 'doesAppOngoingUninstallTaskExist' ||
          item === 'doesAppOngoingRepairTaskExist' ? (
            <StyleProps
              variant={camelCaseToKebabCase(item)}
              tab="true"
              pseudoclass={pseudoclass}
            />
          ) : (
            <TrueFalseTabs
              trueTabChildren={
                <StyleProps
                  variant={camelCaseToKebabCase(item)}
                  tab="true"
                  pseudoclass={pseudoclass}
                />
              }
              falseTabChildren={
                <StyleProps
                  variant={camelCaseToKebabCase(item)}
                  tab="false"
                  pseudoclass={pseudoclass}
                />
              }
            />
          )}
        </div>
      </AccordionContent>
    </RadixAccordion.Item>
  );
};

const AccordionTrigger = forwardRef<
  HTMLButtonElement,
  {
    children: React.ReactNode;
    className?: string;
    iconClassName?: string;
    iconSize?: number;
  }
>(
  (
    { children, className, iconClassName, iconSize, ...props },
    forwardedRef
  ) => (
    <RadixAccordion.Header className="AccordionHeader">
      <RadixAccordion.Trigger
        className={classNames('AccordionTrigger', className)}
        {...props}
        ref={forwardedRef}
      >
        <div className="AccordionTriggerText">
          {iconClassName && (
            <i className={iconClassName} style={{ fontSize: iconSize }} />
          )}
          {children}
        </div>
        <ChevronDownIcon className="AccordionChevron" aria-hidden />
      </RadixAccordion.Trigger>
    </RadixAccordion.Header>
  )
);

const AccordionContent = forwardRef<
  HTMLDivElement,
  { children: React.ReactNode; className?: string }
>(({ children, className, ...props }, forwardedRef) => (
  <RadixAccordion.Content
    className={classNames('AccordionContent', className)}
    {...props}
    ref={forwardedRef}
  >
    <div className="AccordionContentText">{children}</div>
  </RadixAccordion.Content>
));
